import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import Home from '@/pages/Home';
import Products from '@/pages/Products';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Payment from '@/pages/Payment';
import ThankYou from '@/pages/ThankYou';
import Contact from '@/pages/Contact';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import VerifyOTP from '@/pages/VerifyOTP';
import ResetPassword from '@/pages/ResetPassword';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminProducts from '@/pages/AdminProducts';
import AdminBuyers from '@/pages/AdminBuyers';
import AdminBlog from '@/pages/admin/AdminBlog';
import LeadsDashboard from '@/pages/admin/LeadsDashboard';
import AdminReviewsDashboard from '@/pages/AdminReviewsDashboard';
import SocialMediaDashboard from '@/pages/SocialMediaDashboard';
import Profile from '@/pages/Profile';
import Policies from '@/pages/Policies';
import LocationPage from '@/pages/LocationPage';
import PressKit from '@/pages/PressKit';
import BulkInquiry from '@/pages/BulkInquiry';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import HomePremium from '@/pages/HomePremium';
import AIInfo from '@/pages/AIInfo';
import AboutUs from '@/pages/AboutUs';
import ProjectsPage from '@/pages/Projects';
import ProjectDetailPage from '@/pages/ProjectDetail';
import Legacy from '@/pages/Legacy';
import NotFound from '@/pages/NotFound';
import HtmlSitemap from '@/pages/HtmlSitemap';
import PricingGuide from '@/pages/PricingGuide';
import ComparisonGuide from '@/pages/ComparisonGuide';
import CommercialLocationPage from '@/pages/CommercialLocationPage';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { ChatProvider } from '@/context/ChatContext';
import { HomeRedirect } from '@/components/HomeRedirect';
import { SEOHiddenContent } from '@/components/SEOHiddenContent';
import { initAICitationTracker } from '@/lib/aiCitationTracker';
import { AIChatWidget } from '@/components/AIChatWidget';
import { FloatingActions } from '@/components/FloatingActions';
import { ScrollToTop } from '@/components/ScrollToTop';
import './App.css';

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Initialize AI Citation Tracker
  useEffect(() => {
    initAICitationTracker();
  }, []);

  // Disable right-click context menu on images to prevent image saving
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Check if right-click is on an image
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    // Prevent drag on images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <ChatProvider>
            <BrowserRouter>
              <ScrollToTop />
              <ThemeProvider>
                <div className="App">
                  <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/home-classic" element={<Home />} />
                    <Route path="/home-premium" element={<HomePremium />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/product/:slug" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/payment/:orderId" element={<Payment />} />
                    <Route path="/thank-you/:orderId" element={<ThankYou />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/verify-otp" element={<VerifyOTP />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/products" element={<AdminProducts />} />
                    <Route path="/admin/buyers" element={<AdminBuyers />} />
                    <Route path="/admin/blog" element={<AdminBlog />} />
                    <Route path="/admin/leads" element={<LeadsDashboard />} />
                    <Route path="/admin/reviews" element={<AdminReviewsDashboard />} />
                    <Route path="/admin/social-media" element={<SocialMediaDashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/press-kit" element={<PressKit />} />
                    <Route path="/bulk-inquiry" element={<BulkInquiry />} />
                    {/* Blog Routes */}
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    {/* Projects Routes */}
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                    {/* AI Info Page for AI Assistants */}
                    <Route path="/ai-info" element={<AIInfo />} />
                    {/* About Us Page */}
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/legacy" element={<Legacy />} />
                    {/* Programmatic SEO Routes */}
                    <Route path="/office-furniture/:location" element={<LocationPage />} />
                    <Route path="/office-furniture/:location/:category" element={<LocationPage />} />
                    {/* Commercial Location Landing Pages */}
                    <Route path="/office-furniture-bkc-mumbai" element={<CommercialLocationPage locationKey="bkc-mumbai" />} />
                    <Route path="/office-furniture-andheri-powai" element={<CommercialLocationPage locationKey="andheri-powai" />} />
                    <Route path="/office-furniture-navi-mumbai" element={<CommercialLocationPage locationKey="navi-mumbai" />} />
                    <Route path="/office-furniture-pune" element={<CommercialLocationPage locationKey="pune" />} />
                    <Route path="/office-furniture-bengaluru" element={<CommercialLocationPage locationKey="bengaluru" />} />
                    <Route path="/office-furniture-hyderabad" element={<CommercialLocationPage locationKey="hyderabad" />} />
                    <Route path="/office-furniture-gurugram" element={<CommercialLocationPage locationKey="gurugram" />} />
                    <Route path="/office-furniture-mumbai" element={<CommercialLocationPage locationKey="mumbai" />} />
                    <Route path="/office-furniture-thane" element={<CommercialLocationPage locationKey="thane" />} />
                    <Route path="/office-furniture-chennai" element={<CommercialLocationPage locationKey="chennai" />} />
                    <Route path="/office-furniture-ahmedabad" element={<CommercialLocationPage locationKey="ahmedabad" />} />
                    {/* New SEO Pages */}
                    <Route path="/sitemap" element={<HtmlSitemap />} />
                    <Route path="/pricing-guide" element={<PricingGuide />} />
                    <Route path="/comparison-guide" element={<ComparisonGuide />} />
                    {/* 404 — must be last */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <SEOHiddenContent />
                  <AIChatWidget isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
                  <FloatingActions />
                  <Toaster />
                </div>
              </ThemeProvider>
            </BrowserRouter>
          </ChatProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
